import { createContext, useContext, useEffect, useMemo, useState } from "react";
import {
  friendlyAuthError,
  isAdmin,
  login as doLogin,
  logout as doLogout,
  subscribeAuth,
} from "@/lib/auth";

const AdminAuthContext = createContext(null);

export function AdminAuthProvider({ children }) {
  const [user, setUser] = useState(undefined);

  useEffect(() => {
    const unsubscribe = subscribeAuth((nextUser) => {
      setUser(nextUser || null);
    });

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, []);

  const value = useMemo(
    () => ({
      session: user
        ? {
            ...user,
            name: "Operations Admin",
            role: "Super Admin",
          }
        : null,

      checking: user === undefined,

      isAuthenticated: !!user && isAdmin(user),

      login: async ({ email, password }) => {
        try {
          const authed = await doLogin(email, password);
          setUser(authed);
          return authed;
        } catch (err) {
          throw new Error(friendlyAuthError(err));
        }
      },

      logout: async () => {
        await doLogout().catch(() => {});
        setUser(null);
      },
    }),
    [user]
  );

  return (
    <AdminAuthContext.Provider value={value}>
      {children}
    </AdminAuthContext.Provider>
  );
}

export function useAdminAuth() {
  const context = useContext(AdminAuthContext);

  if (!context) {
    throw new Error(
      "useAdminAuth must be used within AdminAuthProvider."
    );
  }

  return context;
}

export const adminCredentials = {
  email: process.env.REACT_APP_ADMIN_EMAIL || "",
  password: "",
};